/**
 * Success feedback for completed API calls — wired from HttpClient, rendered
 * by ToastProvider. Kept React-free so client.ts can import it.
 */

import { stripRuleReferences } from './strip-rule-references.ts';

export interface RequestSuccessInfo {
  readonly method: string;
  readonly path: string;
  readonly body: unknown;
}

export const REQUEST_SUCCESS_EVENT = 'affdice:request-success';

export function notifyRequestSuccess(info: RequestSuccessInfo): void {
  const target = typeof globalThis.window !== 'undefined' ? globalThis.window : globalThis;
  if (typeof target.dispatchEvent !== 'function') return;
  target.dispatchEvent(new CustomEvent(REQUEST_SUCCESS_EVENT, { detail: info }));
}

/** Show a success toast directly — e.g. when the caller already has the message. */
export function showSuccessToast(message: string): void {
  notifyRequestSuccess({ method: 'POST', path: '', body: { message } });
}

export const REQUEST_ERROR_EVENT = 'affdice:request-error';

/**
 * Show a failure toast. Separate event from the success one so a caller
 * cannot raise a red toast by accident, and so the provider can give it
 * `role="alert"` — a message saying why a button did nothing has to be
 * announced, not merely displayed.
 */
export function showErrorToast(message: string): void {
  const target = typeof globalThis.window !== 'undefined' ? globalThis.window : globalThis;
  if (typeof target.dispatchEvent !== 'function') return;
  target.dispatchEvent(new CustomEvent(REQUEST_ERROR_EVENT, { detail: { message } }));
}

/** Many write endpoints answer with `{ message: '...' }` — use it when present. */
export function messageFromSuccessBody(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const o = body as Record<string, unknown>;

  if (typeof o.message === 'string' && o.message.trim() !== '') {
    return stripRuleReferences(o.message.trim());
  }

  // POST /v1/admin/statements — success body has no `message`. Without this,
  // the toast falls through to "Saved." while Payouts stays empty whenever
  // only a carry-forward statement (no invoice) was issued.
  if (typeof o.reason === 'string' && o.reason.trim() !== '') {
    const reason = stripRuleReferences(o.reason.trim());
    const st = typeof o.statement_no === 'string' ? o.statement_no : null;
    return st
      ? `Statement ${st} raised. ${reason} Nothing appears under Awaiting payment until an invoice is issued.`
      : reason;
  }
  if (typeof o.statement_no === 'string' || typeof o.statement_id === 'string') {
    const st = typeof o.statement_no === 'string' ? o.statement_no : String(o.statement_id);
    if (typeof o.invoice_no === 'string' || typeof o.invoice_id === 'string') {
      const inv = typeof o.invoice_no === 'string' ? o.invoice_no : String(o.invoice_id);
      return `Statement ${st} raised · invoice ${inv} is awaiting payment.`;
    }
    return (
      `Statement ${st} raised — no invoice (payable below minimum or nothing payable). ` +
      'Nothing appears under Awaiting payment until an invoice is issued.'
    );
  }
  if (o.mode === 'PER_BRAND' && Array.isArray(o.statements)) {
    return `Statements raised for ${o.statements.length} brands.`;
  }

  /*
   * Many writes answer with a `note` rather than a `message` — proposing deal
   * terms replies "Proposed, not live. Commission continues on the last
   * accepted version until the affiliate accepts them", and the console threw
   * that away and said "Saved." A sentence written for this exact act beats
   * the fallback every time.
   *
   * Length-capped, because `note` is also where a read explains its own
   * payload, at paragraph length. A read never reaches here — GETs do not
   * toast — but a write that answers with an essay would bury the screen, and
   * that is a worse failure than the generic word it replaces.
   */
  if (typeof o.note === 'string') {
    const note = stripRuleReferences(o.note.trim());
    if (note !== '' && note.length <= 240) return note;
  }

  return undefined;
}

/*
 * Writes nobody asked for.
 *
 * "Saved." answers an act: the reader pressed something, and the toast says it
 * took. The notification endpoints below are not acts — they are the console
 * keeping its own unread dots honest. Opening Disputes, Approvals,
 * Reconciliation, Fraud, or Setup → Periods/Payouts marks that area seen, so
 * merely walking the rail raised a "Saved." per tab and stacked three of them
 * on a console left open. Marking one notification read, or all of them, is
 * the same kind of write: the dot going out is the feedback, and a toast on
 * top of it says a thing was saved that the reader never typed.
 *
 * Skipped here rather than at each call site so every console gets it, and so
 * the rule reads as one list of "this path is housekeeping" rather than as a
 * flag threaded through the client.
 */
const SKIP_SUCCESS_TOAST: readonly RegExp[] = [
  /^\/v1\/logout$/,
  /^\/v1\/admin\/notifications\/seen$/,
  /^\/v1\/admin\/notifications\/read-all$/,
  /^\/v1\/admin\/notifications\/[^/]+\/read$/,
  // The intro tour recording itself as seen — opening a tour is not a save.
  /^\/v1\/(admin\/|platform\/)?me\/tour$/,
];

export function shouldToastSuccess(method: string, path: string): boolean {
  if (method === 'GET' || method === 'HEAD') return false;
  const bare = path.split('?')[0] ?? path;
  return !SKIP_SUCCESS_TOAST.some((re) => re.test(bare));
}

/*
 * One act, one toast.
 *
 * Every write raises a success toast, which was exactly right while every Save
 * on a screen made exactly one request. A form that stages its changes and
 * commits them together makes up to eight — and eight toasts stacked four
 * seconds deep bury the screen while saying nothing the caller's own summary
 * does not say better.
 *
 * Counted rather than flagged, so a nested batch cannot un-suppress its parent
 * on the way out, and restored in a `finally` so a refusal mid-way does not
 * leave the console permanently silent.
 *
 * Failures are never suppressed. They do not come through here — they are
 * raised by whoever catches them — which is the property that makes this safe:
 * the one thing a reader must see at the moment it happens still reaches them.
 */
let batching = 0;

export async function asOneAct<T>(run: () => Promise<T>): Promise<T> {
  batching += 1;
  try {
    return await run();
  } finally {
    batching -= 1;
  }
}

export function maybeNotifyRequestSuccess(
  method: string,
  path: string,
  body: unknown,
): void {
  if (batching > 0) return;
  if (!shouldToastSuccess(method, path)) return;
  notifyRequestSuccess({ method, path, body });
}
